import { useAppNotifications } from "@/hooks/useAppNotifications";
import { TOAST_AUTO_HIDE_DURATION } from "@/constants";
import { useAccount } from "@/context/account";
import type { DeviceIdentity } from "@/components/DeviceSerialLink";
import { gql } from "@/gql";
import { RecentlyViewedEntityType, UiFeatureFlagNames } from "@/gql/graphql";
import { useFeatureFlags } from "@/context/FeatureFlags";
import {
  RECENTLY_VIEWED_ITEMS,
  RECORD_RECENTLY_VIEWED_ITEM,
} from "@/hooks/service/recentlyViewedOperations";
import { getDeviceCustomerName } from "@/utils/recentlyViewed";
import { useDeviceToastMessage } from "@/hooks/useDeviceToastMessage";
import { showErrorToast } from "@/utils/showErrorToast";
import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";

const REQUEST_DEVICE_TRANSFER = gql(`
  mutation RequestDeviceTransfer($dealerAccountId: ID!, $dealerNote: String, $iamDeviceId: ID!) {
    requestDeviceTransfer(
      dealerAccountId: $dealerAccountId
      dealerNote: $dealerNote
      iamDeviceId: $iamDeviceId
    ) {
      id
      deviceId
      expiresAt
      transferStatus
    }
  }
`);

type RequestedDevice = DeviceIdentity &
  Parameters<typeof getDeviceCustomerName>[0] & { id: string };

const useRequestDeviceTransfer = () => {
  const { account } = useAccount();
  const { canUseFeatureFlag } = useFeatureFlags();
  const [recordVisit] = useMutation(RECORD_RECENTLY_VIEWED_ITEM, {
    context: { skipGlobalError: true },
    refetchQueries: [RECENTLY_VIEWED_ITEMS],
  });
  const { t } = useTranslation();
  const notifications = useAppNotifications();
  const withDeviceLink = useDeviceToastMessage();

  const [mutate, { loading }] = useMutation(REQUEST_DEVICE_TRANSFER, {
    variables: {
      dealerAccountId: account.id,
    },
    onError: (error) => {
      showErrorToast(
        notifications,
        error,
        t("devices.add_device_modal.transfer_request_error")
      );
    },
  });

  const requestDeviceTransfer = async (
    device: RequestedDevice,
    dealerNote?: string
  ) => {
    const trimmedNote = dealerNote?.trim();
    const { error } = await mutate({
      variables: {
        iamDeviceId: device.id,
        dealerNote: trimmedNote === "" ? undefined : trimmedNote,
      },
    });
    if (error) {
      throw error instanceof Error ? error : new Error(error.message);
    }
    if (canUseFeatureFlag(UiFeatureFlagNames.RecentlyViewed)) {
      void recordVisit({
        variables: {
          input: {
            dealerAccountId: account.id,
            entityType: RecentlyViewedEntityType.Device,
            iamDeviceId: device.id,
            model: device.modelName,
            serialNumber: device.serialNumber,
            customerName: getDeviceCustomerName(device),
          },
        },
      });
    }
    notifications.show(
      withDeviceLink(t("devices.add_device_modal.device_requested"), device),
      {
        severity: "success",
        autoHideDuration: TOAST_AUTO_HIDE_DURATION,
      }
    );
  };

  return [requestDeviceTransfer, { loading }] as const;
};

export { useRequestDeviceTransfer };
